import { useState, useEffect, useCallback } from 'react';
import { FileStorageService } from '../../../services/FileStorageService';
import { ParkingTestViewModel } from '../../../viewmodels/ParkingTestViewModel';
import { AvailableFolders, ParkingTestState } from '../types';

interface UseLearningDataProps {
  projectId: string;
  setState: React.Dispatch<React.SetStateAction<ParkingTestState>>;
}

export function useLearningData({ projectId, setState }: UseLearningDataProps) {
  const [availableFolders, setAvailableFolders] = useState<AvailableFolders>({
    learning: [],
    test: [],
    roi: []
  });
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadAvailableFolders = useCallback(async () => {
    try {
      const [learningResponse, testResponse, roiResponse] = await Promise.all([
        FileStorageService.listFolders(projectId, 'learning'),
        FileStorageService.listFolders(projectId, 'test'),
        FileStorageService.listFiles(projectId, 'roi')
      ]);

      const learningFolders = learningResponse.data.items
        .filter(item => item.isFolder)
        .map(folder => folder.name);

      const testFolders = testResponse.data.items
        .filter(item => item.isFolder)
        .map(folder => folder.name);

      const roiFolders = roiResponse.data.files.map(file => file.filename);

      setAvailableFolders({
        learning: learningFolders,
        test: testFolders,
        roi: roiFolders
      });
    } catch (error) {
      console.error('폴더 목록 로드 실패:', error);
    }
  }, [projectId]);

  const loadLearningHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setState(prev => ({ ...prev, selectedHistoryResults: null }));

      const history = await ParkingTestViewModel.loadLearningHistory(projectId);

      setState(prev => ({
        ...prev,
        learningHistory: history
      }));
    } catch (error) {
      console.error('학습 히스토리 로드 실패:', error);
      setState(prev => ({ ...prev, error: '학습 히스토리를 불러오는데 실패했습니다.' }));
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, setState]);

  useEffect(() => {
    loadAvailableFolders();
    loadLearningHistory();
  }, [loadAvailableFolders, loadLearningHistory]);

  return {
    availableFolders,
    historyLoading,
    loadLearningHistory,
  };
}
